import React, { useCallback, useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/utils/authUtils';

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB — matches backend cap

/**
 * Drag-and-drop cover image picker for the blog editor.
 *
 * Behavior
 * --------
 * - **Editing an existing post** (postId present): file is uploaded
 *   immediately to /api/blog/admin/posts/{id}/cover-image.
 * - **New post** (no postId yet): file is "staged" via `onStageFile` so
 *   the parent BlogEditorPage can hold it in state and upload it the
 *   moment the post is first saved. Preview is shown from the local
 *   blob URL so the admin sees their selection immediately.
 * - Accepts PNG/JPG/JPEG/WEBP. Any other type is rejected client-side.
 */
export const BlogCoverDropzone = ({
  postId,
  coverImage,
  onUploaded,
  onCleared,
  onStageFile,
  stagedPreviewUrl,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const resolveSrc = (raw) => {
    if (!raw) return null;
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
    return raw; // site-relative path — browser resolves it against the host
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Please choose a PNG, JPG, or WEBP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 8 MB.`);
      return;
    }

    // No post saved yet → stage the file in parent state for upload-on-first-save
    if (!postId) {
      if (onStageFile) {
        onStageFile(file);
        toast.success('Cover staged — it will upload when you save the post.');
      } else {
        toast.error('Save the post first, then you can upload a cover.');
      }
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/blog/admin/posts/${postId}/cover-image`, {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Upload failed');
        return;
      }
      onUploaded?.(data.cover_image);
      toast.success(`Cover saved (${data.dimensions})`);
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  }, [postId, onUploaded, onStageFile]);

  const handleClear = useCallback(async () => {
    // Staged file (no postId) → just clear the staged preview
    if (!postId) {
      if (onStageFile) onStageFile(null);
      return;
    }
    if (!window.confirm('Remove this cover image? The auto-generated card will be used instead.')) return;
    setUploading(true);
    try {
      const res = await fetch(`/api/blog/admin/posts/${postId}/cover-image`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to remove cover');
        return;
      }
      onCleared?.();
      toast.success('Cover removed');
    } catch (e) {
      toast.error(`Remove failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  }, [postId, onCleared, onStageFile]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const previewSrc = resolveSrc(stagedPreviewUrl || coverImage);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={[
        'mt-1 relative rounded-lg border-2 border-dashed transition-colors',
        dragOver ? 'border-[#00E5CC] bg-[#00E5CC]/5' : 'border-muted-foreground/30',
        'cursor-pointer hover:border-[#00E5CC]/60',
      ].join(' ')}
      onClick={() => !uploading && inputRef.current?.click()}
      data-testid="blog-cover-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="blog-cover-file-input"
      />

      {previewSrc ? (
        <div className="p-3">
          <div className="relative w-full max-h-64 overflow-hidden rounded-md bg-muted/30">
            <img
              src={previewSrc}
              alt="Cover preview"
              className="w-full h-auto max-h-64 object-contain mx-auto"
              data-testid="blog-cover-preview-image"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-muted-foreground truncate flex-1">
              {stagedPreviewUrl ? 'Staged — will upload on save' : coverImage}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              disabled={uploading}
              className="ml-2 text-destructive hover:underline inline-flex items-center gap-1"
              data-testid="blog-cover-remove-btn"
            >
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="py-8 px-4 text-center">
          {uploading ? (
            <>
              <Loader2 className="h-7 w-7 mx-auto text-[#00E5CC] animate-spin" />
              <p className="text-sm text-muted-foreground mt-2">Uploading…</p>
            </>
          ) : (
            <>
              <Upload className="h-7 w-7 mx-auto text-muted-foreground" />
              <p className="text-sm font-medium mt-2">
                Drag &amp; drop a cover image, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, or WEBP &middot; up to 8&nbsp;MB &middot; auto-converted to JPEG
              </p>
              {!postId && (
                <p className="text-[11px] text-muted-foreground mt-2 italic">
                  (New post — your selection will upload when you save the draft.)
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogCoverDropzone;
